import React, { useEffect, useState } from 'react';
import { Button, Grid, Typography, Box, TextField } from '@mui/material';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { Constants } from '../../const';

const validationSchema = Yup.object({
  otp: Yup.string()
    .required('Please enter the OTP sent on your registered email ID')
    .matches(/^\d+$/, 'Invalid number')
    .length(6, 'Must be exactly 6 digits'),
});
/**
 * @function Otp
 * @description a component to render the UI of Otp form
 * @param {function} LoginOtp - function for logging in through the Otp
 * @returns {React.ReactElement} return a component to render the UI of Otp form
 * @example <Otp loginOtp={loginOtp}/>
 */
const Otp = ({ loginOtp, loginForm, login }) => {
  const history = useHistory();
  const [timer, setTimer] = useState(0);
  const { accountIds } = Constants.CommonConst;
  const otpForm = useFormik({
    initialValues: {
      otp: '',
      password: loginForm?.values?.password || '',
    },
    onSubmit: (values) => {
      loginOtp(values, history);
    },
    validationSchema,
  });

  useEffect(() => {
    let countdown;
    if (timer > 0) {
      countdown = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
    }

    return () => clearInterval(countdown);
  }, [timer]);

  const formatTime = (seconds) => {
    const m = String(Math.floor(seconds / 60)).padStart(2, '0');
    const s = String(seconds % 60).padStart(2, '0');
    return `${m}:${s}`;
  };

  const handleResendOtp = () => {
    if (timer === 0) {
      login(loginForm?.values, 'otp')?.then(() => {
        setTimer(60);
      });
    }
  };

  return (
    <Box>
      <Grid
        container
        display="flex"
        justifyContent="center"
        alignItems="center"
      >
        <Grid item xs={10} sm={9}>
          <Box mb={1}>
            <Typography color="primary" variant="body1" noWrap={true}>
              Please enter the OTP sent on your registered email ID
            </Typography>
          </Box>
        </Grid>
      </Grid>
      <form onSubmit={otpForm.handleSubmit}>
        <Grid
          container
          display="flex"
          justifyContent="center"
          alignItems="center"
          spacing={{ xs: 2, md: 3 }}
        >
          <Grid item xs={10} sm={9}>
            <TextField
              placeholder="Enter OTP"
              variant="outlined"
              fullWidth
              name="otp"
              inputProps={{
                maxLength: 6,
              }}
              label=""
              InputLabelProps={{ shrink: false }}
              error={Boolean(otpForm.touched.otp && otpForm.errors.otp)}
              helperText={otpForm.touched.otp && otpForm.errors.otp}
              {...otpForm.getFieldProps('otp')}
              type="text"
              id="outlined-basic"
            />
          </Grid>
          <Grid item xs={10} sm={9}>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
            >
              <Button
                type="submit"
                variant="contained"
                color="primary"
                size="large"
              >
                Submit
              </Button>
              <Box>
                {accountIds?.includes(loginForm?.values?.accountId) ? (
                  <Typography
                    color="primary"
                    noWrap={true}
                    sx={{
                      cursor: timer === 0 ? 'pointer' : 'default',
                      opacity: timer === 0 ? 1 : 0.6,
                    }}
                    onClick={handleResendOtp}
                  >
                    {timer > 0
                      ? `Resend OTP in ${formatTime(timer)}`
                      : 'Resend OTP?'}
                  </Typography>
                ) : (
                  <Typography color="primary" noWrap={true}>
                    Resend OTP?
                  </Typography>
                )}
              </Box>
            </Box>
          </Grid>
        </Grid>
      </form>
    </Box>
  );
};

export default Otp;

Otp.propTypes = {
  loginOtp: PropTypes.func.isRequired,
  login: PropTypes.func.isRequired,
  loginForm: PropTypes.shape({
    values: PropTypes.shape({
      password: PropTypes.string,
      accountId: PropTypes.string,
    }).isRequired,
  }).isRequired,
};
